<template>
  <!-- Nothing dismisses the modal while the save is in flight: the failure is
       answered in here, and a modal that went would swallow it. -->
  <SfModalPortal
    modal-id="reports/save-period-set-modal"
    size="xs"
    :close-on-escape="!saving"
    :close-on-backdrop-click="!saving"
    :hide-close="saving"
    @opened="openModal"
    @close="closeModal"
  >
    <template #title>{{ copy.title }}</template>

    <template #body>
      <div class="tw-flex tw-flex-col tw-gap-3">
        <!-- Overwrite the set the drawer is on. Only ever a set this firm saved
             itself: the report's own columns and the sets that come with the
             report belong to the firm's template, which Report Settings owns. -->
        <div
          class="tw-rounded-lg tw-border-2 tw-border-solid tw-p-4"
          :class="[
            selectedOption === 'update'
              ? 'tw-border-sf-primary tw-bg-sf-primary-subtle'
              : 'tw-border-sf-neutral-lighter-2 tw-bg-white',
            canUpdate ? '' : 'tw-opacity-60',
          ]"
        >
          <label
            class="tw-flex tw-items-start tw-gap-3"
            :class="canUpdate ? 'tw-cursor-pointer' : 'tw-cursor-not-allowed'"
            data-testid="update-period-set-option"
          >
            <input
              v-model="selectedOption"
              type="radio"
              name="period-set-option"
              value="update"
              :disabled="!canUpdate"
              class="tw-mt-0.5"
            />
            <div class="tw-flex-1">
              <div class="tw-text-base tw-font-medium">
                {{ updateTitle }}
              </div>
              <div
                class="tw-text-sm"
                :class="
                  selectedOption === 'update'
                    ? 'tw-text-sf-primary-dark'
                    : 'tw-text-sf-neutral-light-2'
                "
              >
                {{ copy.update.subtitle }}
              </div>
            </div>
          </label>

          <!-- Outside the label: a label may not hold another one, and SfInput
               brings its own. -->
          <div v-if="selectedOption === 'update'" class="tw-mt-3 tw-pl-6">
            <SfInput
              v-model="updateName"
              focused
              :label="copy.nameLabel"
              data-testid="update-period-set-name-input"
            />
          </div>
        </div>

        <!-- A set of their own. The only option on the report's own columns. -->
        <div
          class="tw-rounded-lg tw-border-2 tw-border-solid tw-p-4"
          :class="
            selectedOption === 'create'
              ? 'tw-border-sf-primary tw-bg-sf-primary-subtle'
              : 'tw-border-sf-neutral-lighter-2 tw-bg-white'
          "
        >
          <label
            class="tw-flex tw-cursor-pointer tw-items-start tw-gap-3"
            data-testid="new-period-set-option"
          >
            <input
              v-model="selectedOption"
              type="radio"
              name="period-set-option"
              value="create"
              class="tw-mt-0.5"
            />
            <div class="tw-flex-1">
              <div class="tw-text-base tw-font-medium">
                {{ copy.new.title }}
              </div>
              <div
                class="tw-text-sm"
                :class="
                  selectedOption === 'create'
                    ? 'tw-text-sf-primary-dark'
                    : 'tw-text-sf-neutral-light-2'
                "
              >
                {{ copy.new.subtitle }}
              </div>
            </div>
          </label>

          <div v-if="selectedOption === 'create'" class="tw-mt-3 tw-pl-6">
            <SfInput
              v-model="newName"
              focused
              :label="copy.nameLabel"
              data-testid="new-period-set-name-input"
            />
          </div>
        </div>

        <p
          v-if="saveError !== null"
          role="alert"
          class="tw-m-0 tw-text-sm tw-text-sf-danger"
          data-testid="save-period-set-error"
        >
          {{ saveMessage }}
        </p>
      </div>
    </template>

    <template #footer>
      <SfButtonDs variant="secondary" :disabled="saving" @click="closeModal">
        {{ copy.cancelButton }}
      </SfButtonDs>
      <SfButtonDs
        variant="primary"
        :disabled="saveDisabled"
        :loading="saving"
        data-testid="save-period-set-submit"
        @click="handleSave"
      >
        {{ copy.saveButton }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import type { Payload, SaveParams } from '../save-period-set-modal'
import type { SaveError } from '../types'

import { computed, ref } from 'vue'

import { saveErrorFrom } from '@app/application/report/api/columns'
import useI18n from '@app/shared/vue/composables/use-I18n'

import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import { SfButtonDs, SfInput } from '@silverfin/ui-components'

import { useColumnEditorI18n } from '../use-column-editor-i18n'

const t = useColumnEditorI18n()
const I18n = useI18n()

const copy = {
  error: I18n.t('common.error.try_again'),
  tooManyColumns: t('save_modal.too_many_columns'),
  title: t('save_modal.title'),
  nameLabel: t('save_modal.name_label'),
  saveButton: t('save_modal.save_button'),
  cancelButton: I18n.t('common.cancel'),
  update: {
    subtitle: t('save_modal.update.subtitle'),
    titleWithoutName: t('save_modal.update.title_without_name'),
  },
  new: {
    title: t('save_modal.new.title'),
    subtitle: t('save_modal.new.subtitle'),
  },
}

const canUpdate = ref(false)
const saving = ref(false)
// null until a save fails.
const saveError = ref<SaveError | null>(null)
const currentName = ref('')
const selectedOption = ref<SaveParams['mode']>('create')
const updateName = ref('')
const newName = ref('')

// The report's own columns have no set behind them, so there is no name to
// quote in the title.
const updateTitle = computed(() =>
  currentName.value
    ? t('save_modal.update.title', { name: currentName.value })
    : copy.update.titleWithoutName
)

const saveMessage = computed(() => {
  switch (saveError.value?.kind) {
    case 'name':
      return saveError.value.message
    case 'too_many_columns':
      return copy.tooManyColumns
    default:
      return copy.error
  }
})

const saveDisabled = computed(
  () =>
    saving.value ||
    (selectedOption.value === 'update'
      ? !updateName.value.trim()
      : !newName.value.trim())
)

let modalPayload: Payload

// Overwriting is the default when it is on offer: the user is looking at their
// own set and saving again almost always means updating it.
function openModal(localModalPayload: Payload): void {
  modalPayload = localModalPayload

  canUpdate.value = localModalPayload.canUpdate
  currentName.value = localModalPayload.periodSet?.name ?? ''
  updateName.value = currentName.value
  newName.value = ''
  selectedOption.value = localModalPayload.canUpdate ? 'update' : 'create'
  saving.value = false
  saveError.value = null
}

// Stays open until the save is through. A save that failed is answered here,
// under a name the user can change, rather than in the drawer behind it. On
// success the page reloads through the saved set, which takes the modal with it.
async function handleSave(): Promise<void> {
  const name =
    selectedOption.value === 'update' ? updateName.value : newName.value

  saving.value = true
  saveError.value = null

  try {
    await modalPayload.onSave({ mode: selectedOption.value, name: name.trim() })
  } catch (error) {
    saving.value = false
    // handleServerError camelizes the body whatever the request asked for.
    saveError.value = saveErrorFrom(error)
  }
}

function closeModal(): void {
  window.modalCitizen.closeModalPortal()
}
</script>

<style scoped>
/* The design system label is bold; the name field here reads as a plain field. */
:deep(.sf-input__label) {
  font-weight: 400;
}
</style>
